import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import NumberInput from '../ui/NumberInput';
import { formatCurrency, formatDate } from '../../lib/utils';
import { cn } from '../../lib/utils';

const CategoryBottomSheet = ({
 category,
 budgeted,
 spent,
 remaining,
 transactions,
 onClose,
 onAddExpense,
 onDeleteTransaction,
 onUpdateBudget,
 onRenameCategory,
 onDeleteCategory,
}) => {
 const [swipedTransaction, setSwipedTransaction] = useState(null);
 const [editingBudget, setEditingBudget] = useState(false);
 const [budgetDraft, setBudgetDraft] = useState('');
 const [editingName, setEditingName] = useState(false);
 const [nameDraft, setNameDraft] = useState('');

 if (!category) return null;

 const handleTouchStart = (txnId, e) => {
 const touch = e.touches[0];
 e.currentTarget.dataset.startX = touch.clientX;
 };

 const handleTouchMove = (txnId, e) => {
 const touch = e.touches[0];
 const startX = parseFloat(e.currentTarget.dataset.startX);
 const deltaX = touch.clientX - startX;
 if (deltaX < -50) setSwipedTransaction(txnId);
 else if (deltaX > 20) setSwipedTransaction(null);
 };

 const commitName = () => {
 const trimmed = nameDraft.trim();
 if (trimmed && trimmed !== category.name && onRenameCategory) {
 onRenameCategory(category.id, trimmed);
 }
 setEditingName(false);
 };

 const commitBudget = () => {
 onUpdateBudget(category.id, parseFloat(budgetDraft) || 0);
 setEditingBudget(false);
 };

 return (
 <div
 className="fixed inset-0 bg-black/50 flex items-end sm:items-stretch sm:justify-end z-50"
 onClick={onClose}
 >
 <div
 className="sheet-enter bg-background w-full max-w-2xl max-h-[85vh] sm:max-w-sm sm:max-h-full sm:h-full overflow-hidden flex flex-col"
 onClick={(e) => e.stopPropagation()}
 >
 {/* ── Header: name + actions ── */}
 <div className="flex items-center justify-between px-4 py-3 border-b border-border">
 <div className="flex-1 min-w-0 mr-2">
 {editingName ? (
 <input
 autoFocus
 value={nameDraft}
 onChange={(e) => setNameDraft(e.target.value)}
 onBlur={commitName}
 onKeyDown={(e) => {
 if (e.key === 'Enter') commitName();
 if (e.key === 'Escape') setEditingName(false);
 }}
 className="w-full text-[11px] font-semibold uppercase tracking-widest bg-transparent border-b border-primary outline-none text-foreground"
 />
 ) : (
 <button
 onClick={() => { setNameDraft(category.name); setEditingName(true); }}
 className="text-[11px] font-semibold uppercase tracking-widest text-foreground hover:opacity-60 transition-opacity text-left w-full truncate"
 >
 {category.name}
 </button>
 )}
 </div>
 <div className="flex items-center gap-1 flex-shrink-0">
 {onDeleteCategory && (
 <Button
 variant="ghost"
 size="icon"
 onClick={() => onDeleteCategory(category.id)}
 className="h-8 w-8 text-muted-foreground hover:text-destructive"
 >
 <Trash2 className="h-4 w-4" />
 </Button>
 )}
 <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
 <X className="h-4 w-4" />
 </Button>
 </div>
 </div>

 {/* ── Budget (large, editable) ── */}
 <div className="px-4 pt-4 pb-3 border-b border-border">
 <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Budget</p>
 {editingBudget ? (
 <NumberInput
 value={budgetDraft}
 onChange={setBudgetDraft}
 onBlur={commitBudget}
 onKeyDown={(e) => {
 if (e.key === 'Enter') commitBudget();
 if (e.key === 'Escape') setEditingBudget(false);
 }}
 hideButtons
 autoFocus
 className="w-full h-10 text-2xl font-semibold font-mono"
 />
 ) : (
 <button
 onClick={() => { setBudgetDraft(budgeted); setEditingBudget(true); }}
 className="text-2xl font-semibold font-mono tabular-nums text-foreground hover:opacity-60 transition-opacity text-left w-full underline decoration-dotted underline-offset-4"
 >
 {formatCurrency(budgeted)}
 </button>
 )}
 </div>

 {/* ── Left + Add Expense ── */}
 <div className="px-4 pt-3 pb-4 border-b border-border">
 <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Left</p>
 <p className={cn(
 "text-xl font-semibold font-mono tabular-nums mb-3",
 remaining < 0 ? "text-destructive" : "text-success"
 )}>
 {formatCurrency(remaining)}
 </p>
 <Button onClick={onAddExpense} className="w-full h-11 text-sm font-medium">
 <Plus className="h-4 w-4 mr-2" />
 Add Expense
 </Button>
 </div>

 {/* ── Transactions list ── */}
 <div className="flex-1 overflow-y-auto">
 <div className="flex items-center justify-between px-4 py-3 border-b border-border">
 <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Transactions</p>
 <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
 Spent: <span className="font-mono tabular-nums normal-case tracking-normal">{formatCurrency(spent)}</span>
 </p>
 </div>

 {transactions.length === 0 ? (
 <div className="text-center py-10">
 <p className="text-sm text-muted-foreground">No transactions yet</p>
 </div>
 ) : (
 <div className="divide-y divide-border">
 {transactions.map((txn) => (
 <div key={txn.id} className="relative overflow-hidden">
 <div
 className="flex items-center justify-between px-4 py-3 bg-background transition-transform duration-200"
 style={{
 transform: swipedTransaction === txn.id ? 'translateX(-80px)' : 'translateX(0)'
 }}
 onTouchStart={(e) => handleTouchStart(txn.id, e)}
 onTouchMove={(e) => handleTouchMove(txn.id, e)}
 >
 <div className="flex-1 min-w-0">
 <p className="text-sm font-medium text-foreground truncate">{txn.payee || 'Expense'}</p>
 <p className="text-xs text-muted-foreground mt-0.5">{formatDate(txn.date)}</p>
 </div>
 <div className="flex items-center gap-1 ml-3 flex-shrink-0">
 <p className="text-base font-semibold font-mono tabular-nums text-foreground">
 {formatCurrency(Math.abs(txn.amount))}
 </p>
 <button
 onClick={(e) => { e.stopPropagation(); onDeleteTransaction(txn.id); setSwipedTransaction(null); }}
 className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
 aria-label="Delete transaction"
 >
 <Trash2 className="h-4 w-4" />
 </button>
 </div>
 </div>
 {swipedTransaction === txn.id && (
 <button
 onClick={() => { onDeleteTransaction(txn.id); setSwipedTransaction(null); }}
 className="absolute right-0 top-0 bottom-0 w-20 bg-destructive flex items-center justify-center"
 aria-label="Delete transaction"
 >
 <Trash2 className="h-5 w-5 text-[hsl(var(--destructive-foreground))]" />
 </button>
 )}
 </div>
 ))}
 </div>
 )}
 </div>
 </div>
 </div>
 );
};

export default CategoryBottomSheet;
